// Package-edit-dialog panel layout: package-specific config over the reusable panel-grid base.
import {
  createPanelGridGroups,
  createPanelGridLayout,
  detachPanelGridPanel,
  mergePanelGridGroups,
  migratePanelGridLayout,
  movePanelGridGroupToNewTabsheet,
  movePanelGridGroupToTabsheet,
  reorderPanelGridTabsheets,
  type PanelGridConfig,
  type PanelGridGroup,
  type PanelGridLayoutState,
  type PanelGridTabsheet,
  type StoredPanelGridLayoutState,
} from '@/common/panelGrid/panelGridLayout';

export type PackageDialogGroupId = 'core' | 'physical' | 'nominal' | 'contents' | 'statistics';

export type PackageDialogGroup = PanelGridGroup;
export type PackageDialogTabsheet = PanelGridTabsheet;
export type PackageDialogGridLayoutState = PanelGridLayoutState;
export type StoredPackageDialogGridLayoutState = StoredPanelGridLayoutState;

export const packageDialogGridSchemaVersion = 3;
export const packageDialogGridColumns = 12;
export const packageDialogGridCellHeight = 34;
export const packageDialogGridMargin = 6;
export const packageDialogCollapsedRows = 1;
export const packageDialogDefaultWidth = 1500;
export const packageDialogDefaultHeight = 860;
export const packageDialogMinWidth = 960;
export const packageDialogMinHeight = 560;
export const packageDialogDefaultLabelWidth = 176;
export const packageDialogMinLabelWidth = 64;
export const packageDialogMaxLabelWidth = 480;
export const packageDialogPanelOrder: PackageDialogGroupId[] = ['core', 'physical', 'nominal', 'contents', 'statistics'];

const placements: Record<PackageDialogGroupId, { x: number; y: number; w: number; h: number }> = {
  core: { x: 0, y: 0, w: 3, h: 14 },
  contents: { x: 3, y: 0, w: 6, h: 14 },
  physical: { x: 9, y: 0, w: 3, h: 14 },
  nominal: { x: 0, y: 14, w: 8, h: 8 },
  statistics: { x: 8, y: 14, w: 4, h: 8 },
};

export const packageDialogGridConfig: PanelGridConfig = {
  schemaVersion: packageDialogGridSchemaVersion,
  columns: packageDialogGridColumns,
  cellHeight: packageDialogGridCellHeight,
  margin: packageDialogGridMargin,
  collapsedRows: packageDialogCollapsedRows,
  window: {
    defaultWidth: packageDialogDefaultWidth,
    defaultHeight: packageDialogDefaultHeight,
    minWidth: packageDialogMinWidth,
    minHeight: packageDialogMinHeight,
    maxWidth: 3840,
    maxHeight: 2160,
  },
  label: {
    default: packageDialogDefaultLabelWidth,
    min: packageDialogMinLabelWidth,
    max: packageDialogMaxLabelWidth,
  },
  panelOrder: packageDialogPanelOrder,
  placements,
};

export function createDefaultPackageDialogGroups(): PackageDialogGroup[] {
  return createPanelGridGroups(packageDialogGridConfig);
}

export function createDefaultPackageDialogGridLayout(): PackageDialogGridLayoutState {
  return createPanelGridLayout(packageDialogGridConfig);
}

export function migratePackageDialogGridLayout(
  stored?: StoredPackageDialogGridLayoutState | null,
): PackageDialogGridLayoutState {
  return migratePanelGridLayout(packageDialogGridConfig, stored);
}

export function detachPackageDialogPanel(
  groups: PackageDialogGroup[],
  groupId: string,
  panelId: PackageDialogGroupId,
): PackageDialogGroup[] {
  return detachPanelGridPanel(packageDialogGridConfig, groups, groupId, panelId);
}

export const mergePackageDialogGroups = mergePanelGridGroups;
export const moveGroupToNewTabsheet = movePanelGridGroupToNewTabsheet;
export const moveGroupToTabsheet = movePanelGridGroupToTabsheet;
export const reorderTabsheets = reorderPanelGridTabsheets;
